import React, { useState, useEffect, MutableRefObject } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Message as MessageType } from './types';
import { useLanguage } from '../../contexts/LanguageContext';
import VoicePlaybackButton from './VoicePlaybackButton';
import {
  MessageWrapper,
  MessageContent,
  SourcesCollapsible,
  SourcesContent,
  SourceLink,
} from './styles';

interface MessageProps {
  message: MessageType;
  isDarkMode: boolean;
  audioCacheRef: MutableRefObject<Map<string, string>>;
  autoPlay: boolean;
  showDisclosure: boolean;
  onAutoPlayDone: () => void;
}

const Message: React.FC<MessageProps> = ({
  message,
  isDarkMode,
  audioCacheRef,
  autoPlay,
  showDisclosure,
  onAutoPlayDone,
}) => {
  const { t } = useLanguage();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  return (
    <MessageWrapper role={message.role} isWelcome={message.isWelcome}>
      <MessageContent
        role={message.role}
        isDarkMode={isDarkMode}
        isWelcome={message.isWelcome}
      >
        {message.role === 'assistant' ? (
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {message.content}
          </ReactMarkdown>
        ) : (
          <div>{message.content}</div>
        )}
      </MessageContent>

      {message.role === 'assistant' && !message.isError && (
        <VoicePlaybackButton
          messageId={message.id}
          text={message.content}
          audioCacheRef={audioCacheRef}
          autoPlay={autoPlay}
          showDisclosure={showDisclosure}
          isDarkMode={isDarkMode}
          onAutoPlayDone={onAutoPlayDone}
        />
      )}

      {message.sources && message.sources.length > 0 && (
        <SourcesCollapsible isDarkMode={isDarkMode}>
          <summary>{t('widget.sources')}</summary>
          <SourcesContent isDarkMode={isDarkMode}>
            {message.sources.map((source, index) => {
              const link = message.sourceLinks?.[index];
              if (link) {
                return (
                  <SourceLink
                    key={index}
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    isDarkMode={isDarkMode}
                  >
                    {source}
                  </SourceLink>
                );
              }
              return <span key={index}>{source}, </span>;
            })}
          </SourcesContent>
        </SourcesCollapsible>
      )}
    </MessageWrapper>
  );
};

export default Message;
